import cn from 'classnames'

import { useEffect, useMemo, useState } from 'react'

import LoansIcon from 'components/ui/account/nabvar-v2-left/icons/Loans.svg'
import SavingsIcon from 'components/ui/account/nabvar-v2-left/icons/Savings.svg'
import WalletIcon from 'components/ui/account/nabvar-v2-left/icons/Wallet.svg'

import IconArrow from 'images/icon-arrow.svg'

import { useTranslation } from 'utils/use-translation'

import { ETransactionStatuses, ETransactionTypes, ITransaction } from '../../../../store/fetchers/users/types'

import DashboardOperationItem from '../operation-item'

import { useAppSelector } from '../../../../store/hooks'

import { selectedUser } from '../../../../store/reducers/users'

import calcDifferenceInDays from '../../../../utils/calc-difference-in-days'

import WalletTitleIcon from './tabs/images/walets-title.svg'
import SavingTitleIcon from './tabs/images/savings-title.svg'
import LoansTitleIcon from './tabs/images/loans-title.svg'

import { OperationCardTabs, TabNames } from './tabs'

import styles from './styles.module.scss'
import { IOperationsCardProps } from './operations-card.props'
import { allTransactions } from 'store/reducers/wallets'

type TabName = typeof TabNames[keyof typeof TabNames]

export default function DashboardOperationsCard({
  toggleVisibility,
  tabsVisibility,
}: IOperationsCardProps) {
  const { t, locale, replacePlaceholders } = useTranslation()

  const allWalletTransactions = useAppSelector(allTransactions)

  const [transactions, setTransactions] = useState([])
  const [activeTab, setActiveTab] = useState<TabName>(TabNames.LOANS)
  const [loansTransactions, setLoansTransactions] = useState<ITransaction[]>([])
  const [walletTransactions, setWalletTransactions] = useState<ITransaction[]>([])
  const [savingsTransactions, setSavingsTransactions] = useState<ITransaction[]>([])
  const user = useAppSelector(selectedUser)

  const tabs = [
    { name: TabNames.LOANS, icon: LoansIcon, count: loansTransactions.length, iconTitle: LoansTitleIcon },
    { name: TabNames.WALLETS, icon: WalletIcon, count: walletTransactions.length, iconTitle: WalletTitleIcon },
    { name: TabNames.SAVINGS, icon: SavingsIcon, count: savingsTransactions.length, iconTitle: SavingTitleIcon },
  ]
  const totalTransactions = tabs.reduce((total, { count }) => total + count, 0)

  const activeTransactions = useMemo(() => {
    switch (activeTab) {
      case TabNames.LOANS:
        return loansTransactions
      case TabNames.WALLETS:
        return walletTransactions
      case TabNames.SAVINGS:
        return savingsTransactions
      default:
        return []
    }
  }, [activeTab, walletTransactions, loansTransactions, savingsTransactions, transactions])

  useEffect(() => {
    setTransactions(activeTransactions)
  }, [activeTransactions])

  useEffect(() => {
    if (user && allWalletTransactions?.length) {
      getTransactions()
    }
  }, [user, allWalletTransactions])

  const getTransactions = async () => {
    const wallets = allWalletTransactions?.filter(
      (item) =>
        item.account_type === 'wallet' &&
        item.status &&
        ((item.status === ETransactionStatuses.PENDING &&
          calcDifferenceInDays(new Date(item.created_at), new Date()) < 1) ||
          item.status === ETransactionStatuses.PROCESSING ||
          item.status === ETransactionStatuses.CONFIRMING ||
          item.status === ETransactionStatuses.REJECTED) &&
        (item.transaction_type === ETransactionTypes.EXCHANGE || item.from_address || item.to_address) &&
        (item.from_amount || item.to_amount),
    )?.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())

    const savings = allWalletTransactions?.filter(
      (item) =>
        item.account_type === 'saving' &&
        item.status &&
        ((item.status === ETransactionStatuses.PENDING &&
          calcDifferenceInDays(new Date(item.created_at), new Date()) < 1) ||
          item.status === ETransactionStatuses.PROCESSING ||
          item.status === ETransactionStatuses.CONFIRMING) &&
        (item.from_amount || item.to_amount) &&
        item.transaction_type !== ETransactionTypes.EXCHANGE,
    )?.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())

    const loans = allWalletTransactions?.filter(
      (item) =>
        item.account_type === 'loan' &&
        item.status &&
        ((item.status === ETransactionStatuses.PENDING &&
          calcDifferenceInDays(new Date(item.created_at), new Date()) < 1) ||
          item.status === ETransactionStatuses.PROCESSING ||
          item.status === ETransactionStatuses.CONFIRMING) &&
        (item.from_amount || item.to_amount) &&
        item.transaction_type !== ETransactionTypes.EXCHANGE,
    )?.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())

    setWalletTransactions(wallets)

    setSavingsTransactions(savings)

    setLoansTransactions(loans)

    if (activeTab === TabNames.LOANS && !loans?.length) {
      handleTabChange(wallets?.length ? TabNames.WALLETS : TabNames.SAVINGS)
    } else if (activeTab === TabNames.WALLETS && !wallets?.length) {
      handleTabChange(loans?.length ? TabNames.LOANS : TabNames.SAVINGS)
    } else if (activeTab === TabNames.SAVINGS && !savings?.length) {
      handleTabChange(loans?.length ? TabNames.LOANS : TabNames.WALLETS)
    }
  }

  const handleTabChange = (tab: TabName) => {
    setActiveTab(tab)
  }

  return tabs.filter((tab) => tab.count !== 0).length > 0 ? (
    <div className={styles.card}>
      <div className={cn(styles['card-header'])}>
        <h2 className={styles.title}>{t('ongoing_transactions')}</h2>
        {transactions.length !== 0 && (
          <div className={styles.card__arrow} onClick={toggleVisibility}>
            <img
              alt="arrow"
              className={cn({
                [styles.close]: !tabsVisibility.tab0,
              })}
              src={IconArrow}
            />
          </div>
        )}
      </div>

      <div
        className={cn(
          styles.card__content,
          { [styles.card__hidden]: !tabsVisibility.tab0 },
          { [styles.card__visible]: tabsVisibility.tab0 },
        )}
      >
        {tabs.filter((tab) => tab.count !== 0).length > 1 && (
          <OperationCardTabs
            activeTab={activeTab}
            onTabChange={handleTabChange}
            tabs={tabs.filter((tab) => tab.count !== 0)}
          />
        )}

        {tabs.filter((tab) => tab.count !== 0).length === 1 && (
          <div className={styles.title}>
            <img src={tabs.filter((tab) => tab.count !== 0)[0].iconTitle} alt="tab icon" />
            <p>{t(tabs?.filter((tab) => tab.count !== 0)[0]?.name?.toLowerCase())}</p>
          </div>
        )}

        <div className={styles.transactions}>
          {transactions.length !== 0 &&
            transactions.map((item, index) => <DashboardOperationItem key={index} transaction={item} />)}
        </div>
      </div>

      {!tabsVisibility.tab0 && (
        <p className={styles.card__text}>
          {locale === 'en' ? (
            <>
              You have <span className={styles.card__text_balance}>{totalTransactions}</span> ongoing{' '}
              {transactions.length > 1 ? 'transaction' : 'transactions'}
            </>
          ) : (
            <>{replacePlaceholders(t('you_have_ongoing_transactions'), { count: totalTransactions })}</>
          )}
        </p>
      )}
    </div>
  ) : null
}
